/**
 * useSubscription - Hook do łatwego dostępu do subskrypcji
 *
 * Przykłady użycia:
 *
 * // Sprawdź konkretną subskrypcję
 * const { hasSubscription } = useSubscription();
 * if (hasSubscription(SUBSCRIPTION_TYPES.PPE_MANAGEMENT)) { ... }
 *
 * // Sprawdź czy ma którąkolwiek subskrypcję
 * const { hasAnySubscription, loading } = useSubscription();
 * if (!loading && hasAnySubscription()) { ... }
 *
 * // Przefiltruj menu
 * const { filterMenu } = useSubscription();
 * const visibleItems = filterMenu(ADMIN_MENU);
 */

import { useContext } from 'react';
import { SubscriptionContext } from '../contexts/SubscriptionContext';
import { SUBSCRIPTION_TYPES } from '../config/subscriptionConfig';

/**
 * Hook do dostępu do funkcji subskrypcji
 * @returns {Object} - Obiekt z funkcjami i stanem subskrypcji
 */
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);

  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }

  return context;
};

/**
 * Hook do sprawdzania konkretnej subskrypcji
 * @param {number} typeId - ID typu subskrypcji
 * @returns {Object} - { hasAccess: boolean, loading: boolean }
 */
export const useSubscriptionCheck = (typeId) => {
  const { hasSubscription, loading } = useSubscription();

  return {
    hasAccess: hasSubscription(typeId),
    loading,
  };
};

/**
 * Hook do sprawdzania czy użytkownik ma dostęp do trasy
 * @param {string} pathname - Ścieżka do sprawdzenia
 * @returns {Object} - { hasAccess: boolean, loading: boolean }
 */
export const useRouteAccess = (pathname) => {
  const { canAccessRoute, loading } = useSubscription();

  return {
    hasAccess: canAccessRoute(pathname),
    loading,
  };
};

/**
 * Hook zwracający przefiltrowane menu dla admina
 * @param {Array} menu - Menu do przefiltrowania
 * @returns {Object} - { filteredMenu: Array, loading: boolean }
 */
export const useFilteredMenu = (menu) => {
  const { filterMenu, loading } = useSubscription();

  return {
    filteredMenu: loading ? [] : filterMenu(menu),
    loading,
  };
};

// Re-export SUBSCRIPTION_TYPES dla wygody
export { SUBSCRIPTION_TYPES };

export default useSubscription;
